import React, { useEffect, useState, useRef, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Badge,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  Icon,
  IconButton,
  SimpleGrid,
  Textarea,
  Link,
  Flex,
  Slider,
  Separator,
  Tooltip,
  Toaster,
  createToaster,
  Spinner
} from '@chakra-ui/react'
import { FiArrowLeft, FiArrowRight, FiChevronDown, FiChevronUp, FiInfo, FiMove, FiX } from 'react-icons/fi'
import { OnboardingLayout } from '../../components/onboarding/OnboardingLayout'
import { CarouselAdPreviewCard } from '../../components/onboarding/CarouselAdPreviewCard'
import { OnboardingSideDrawer } from '../../components/onboarding/drawers/OnboardingSideDrawer'
import { useOnboarding } from '../../contexts/OnboardingContext'
import { apiConfig } from '../../config/api'
import { ImproveableTextarea } from '../../components/onboarding/ImproveableTextarea'
import { InfoTooltip } from '../../components/InfoTooltip'
import type {
  DetailedInterview,
  CarouselCustomization,
  RefinementActionId,
  BasicInfoWithPreview,
  getRefinementFieldValue,
  createRefinementUpdate,
  REFINEMENT_CHAR_LIMITS,
  isOverCharLimit
} from '../../types/preview'
import {
  getRefinementFieldValue as getFieldValue,
  createRefinementUpdate as createUpdate,
  REFINEMENT_CHAR_LIMITS as CHAR_LIMITS,
  isOverCharLimit as checkOverLimit
} from '../../types/preview'

const toaster = createToaster({
  placement: 'top-end',
  pauseOnPageIdle: true,
})

const getImprovementActions = (mode: 'service' | 'product') => {
  const isProduct = mode === 'product'
  return [
    {
      id: 'before_moment' as const,
      title: 'Add the “before” moment',
      description: isProduct
        ? 'What do buyers say right before they try it?'
        : 'What do people say right before they reach out?',
      potentialIncrease: 10
    },
    {
      id: 'turning_point' as const,
      title: 'Add the turning point',
      description: isProduct
        ? 'What makes someone decide it’s time to try something new?'
        : 'What finally makes someone decide to get help?',
      potentialIncrease: 8
    },
    {
      id: 'approach_one_liner' as const,
      title: isProduct ? 'Describe how it works (1 sentence)' : 'Describe your approach (1 sentence)',
      description: 'Plain language, no jargon',
      potentialIncrease: 8
    },
    {
      id: 'first_call_expectations' as const,
      title: isProduct ? 'Set expectations for the first step' : 'Set expectations for the first call',
      description: isProduct ? 'What should they do next after clicking?' : 'Reduce anxiety: what happens next?',
      potentialIncrease: 6
    },
    {
      id: 'testimonial' as const,
      title: 'Add one specific testimonial',
      description: 'Only use real, outcome-specific proof',
      potentialIncrease: 6
    },
    {
      id: 'exclusions' as const,
      title: 'Who is this NOT for?',
      description: 'Helps filter and protects your time',
      potentialIncrease: 6
    },
    {
      id: 'origin_story' as const,
      title: 'Why do you do this work?',
      description: 'Share the moment or experience that led you to this work',
      potentialIncrease: 4
    }
  ]
}

const getStoryTypeTooltip = (templateName: string): string => {
  const tooltips: Record<string, string> = {
    'Transformation Story': "Best for most practitioners. Shows the journey from problem to outcome: who you help, their challenge, and the transformation you deliver.",
    'Qualification First': "Best when exclusions are important. Starts by qualifying the audience, then contrasts wrong approaches with your right approach.",
    'Practitioner Origin': "Best when you have a compelling origin story. Leads with why you do this work, then explains what you help people achieve."
  }
  return tooltips[templateName] || "A narrative approach to telling your story."
}

export const PreviewPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const {
    onboardingMode,
    step,
    generated_assets,
    generatePreview,
    isLoading,
    error,
    basic_info,
    detailed_interview,
    updateDetailedInterview,
    saveDetailedInterviewBlock,
    updateBasicInfo,
    saveBasicSetup,
    isSaving: isSavingBasics,
    getAuthToken
  } = useOnboarding()

  // Get path prefix from current route
  const getPathPrefix = () => {
    if (location.pathname.startsWith('/for-products/onboarding')) return '/for-products/onboarding'
    if (location.pathname.startsWith('/for-services/onboarding')) return '/for-services/onboarding'
    return '/for-services/onboarding' // Default fallback
  }
  const pathPrefix = getPathPrefix()
  const improvementActions = useMemo(() => getImprovementActions(onboardingMode), [onboardingMode])

  const [currentAdIndex, setCurrentAdIndex] = useState(0)
  const [expandedActionId, setExpandedActionId] = useState<RefinementActionId | null>(null)
  const [actionValue, setActionValue] = useState('')
  const [isSavingAction, setIsSavingAction] = useState(false)
  const [hoveredActionId, setHoveredActionId] = useState<RefinementActionId | null>(null)
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const autoSaveSequenceRef = useRef<number>(0)
  const textareaRefs = useRef<{ [key: string]: HTMLTextAreaElement | null }>({})
  const [isSavingPalette, setIsSavingPalette] = useState(false)
  type PreviewDrawerPanel = 'customizeLook' | 'refineStory'
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [activeDrawerPanel, setActiveDrawerPanel] = useState<PreviewDrawerPanel>('customizeLook')
  const [isSavingCustomize, setIsSavingCustomize] = useState(false)
  const [isWhyExpanded, setIsWhyExpanded] = useState(false)
  const [showOriginal, setShowOriginal] = useState(false)
  const [photoShadow, setPhotoShadow] = useState(false)
  const [isReprocessing, setIsReprocessing] = useState(false)
  const [isLargeScreen, setIsLargeScreen] = useState(false)
  const refinementsSectionRef = useRef<HTMLDivElement | null>(null)

  // Detect large screen (lg breakpoint = 1024px) for split layout
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)')
    setIsLargeScreen(mediaQuery.matches)
    const handler = (e: MediaQueryListEvent) => setIsLargeScreen(e.matches)
    mediaQuery.addEventListener('change', handler)
    return () => mediaQuery.removeEventListener('change', handler)
  }, [])
  const didAutoGenerateRef = useRef(false)
  const saveBasicSetupRef = useRef(saveBasicSetup)
  const customizeSequenceRef = useRef<number>(0)

  // Toggle between polished and original carousel candidates based on showOriginal
  const polishedCandidates = generated_assets.carousel_candidates || []
  const originalCandidates = generated_assets.original_carousel_candidates || []
  const carouselCandidates = showOriginal ? originalCandidates : polishedCandidates
  const currentCarousel = carouselCandidates[currentAdIndex]
  const isCustomizePanelOpen = isDrawerOpen && activeDrawerPanel === 'customizeLook'
  const hasOriginalCandidates = originalCandidates.length > 0
  const copyWasPolished = generated_assets.copy_polished === true

  useEffect(() => {
    // If progress load populated candidates, we're good.
    if (carouselCandidates.length > 0) return
    // Avoid firing while a request is in flight.
    if (isLoading) return
    // If an error occurred, don't loop.
    if (error) return
    // Don't auto-generate if no basic setup has been completed (step 0)
    // The route guard should redirect, but this prevents errors during the transition
    if (step === 0) return
    // Auto-generate once per mount.
    if (didAutoGenerateRef.current) return
    didAutoGenerateRef.current = true
    generatePreview().catch((e) => {
      console.error('Failed to generate preview:', e)
    })
  }, [carouselCandidates.length, isLoading, error, step, generatePreview])

  // Get character limit for each action type
  const getActionLimit = (actionId: RefinementActionId): number => {
    return CHAR_LIMITS[actionId] || 300
  }

  const handleActionClick = (actionId: RefinementActionId) => {
    if (expandedActionId === actionId) {
      // Don't close on click - keep it open
      return
    }

    setExpandedActionId(actionId)
    // Set initial value based on current data
    const value = getFieldValue(detailed_interview, actionId)
    setActionValue(value)
  }

  // Auto-save function with debounce and sequence tracking
  const handleAutoSave = async (value: string, actionId: RefinementActionId) => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current)
    }

    autoSaveTimeoutRef.current = setTimeout(async () => {
      if (!actionId || !value.trim()) return

      const currentSequence = ++autoSaveSequenceRef.current

      try {
        const update = createUpdate(actionId, value)
        updateDetailedInterview(update)

        // Only save if this is still the most recent edit
        if (currentSequence === autoSaveSequenceRef.current) {
          await saveDetailedInterviewBlock(update)
        }
      } catch (e) {
        console.error('Failed to auto-save:', e)
        toaster.create({
          title: 'Save failed',
          description: e instanceof Error ? e.message : 'Could not save your refinement. Changes will be retried.',
          type: 'error',
          duration: 5000
        })
      }
    }, 1500) // 1.5 second debounce (unified with customization)
  }

  const handleSaveAction = async () => {
    if (!expandedActionId) return

    setIsSavingAction(true)
    try {
      const update = createUpdate(expandedActionId, actionValue)
      updateDetailedInterview(update)
      await saveDetailedInterviewBlock(update)
      // Keep card open after save - don't close it
    } catch (e) {
      console.error('Failed to save action:', e)
      toaster.create({
        title: 'Save failed',
        description: e instanceof Error ? e.message : 'Could not save your refinement. Please try again.',
        type: 'error',
        duration: 7000
      })
    } finally {
      setIsSavingAction(false)
    }
  }

  // Handle value change with auto-save
  const handleActionValueChange = (value: string, actionId: RefinementActionId) => {
    setActionValue(value)
    handleAutoSave(value, actionId)
  }

  // Auto-resize textarea based on content
  const autoResizeTextarea = (actionId: string) => {
    const textarea = textareaRefs.current[actionId]
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 300)}px` // Max height of 300px
    }
  }

  // Cleanup auto-save timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }
    }
  }, [])

  // Auto-resize textareas when value changes or when they become visible
  useEffect(() => {
    // Small delay to ensure DOM is updated
    const timeout = setTimeout(() => {
      if (expandedActionId) {
        autoResizeTextarea(expandedActionId)
      }
      // Also resize all textareas with content
      improvementActions.forEach((action) => {
        const currentValue = getFieldValue(detailed_interview, action.id)
        if (currentValue.trim().length > 0) {
          autoResizeTextarea(action.id)
        }
      })
    }, 0)
    return () => clearTimeout(timeout)
  }, [actionValue, expandedActionId, detailed_interview, improvementActions])

  const handleRetryGeneration = async () => {
    try {
      await generatePreview()
    } catch (e) {
      console.error('Failed to retry preview generation:', e)
    }
  }

  const handleContinue = () => {
    navigate(`${pathPrefix}/landing-page`)
  }

  const handleBackToBasics = () => {
    navigate(`${pathPrefix}/basic-setup`)
  }

  const selectedPalette = ((basic_info as any)?.carousel_bg_palette || 'brand') as string

  const handleSelectPalette = async (palette: string) => {
    updateBasicInfo({ carousel_bg_palette: palette } as any)
    setIsSavingPalette(true)
    try {
      await saveBasicSetup()
    } catch (e) {
      console.error('Failed to save background palette:', e)
      toaster.create({
        title: 'Save failed',
        description: 'Could not save background color. Please try selecting again.',
        type: 'error',
        duration: 5000
      })
    } finally {
      setIsSavingPalette(false)
    }
  }

  const getCurrentLook = () => {
    return {
      photo_focal_x: (basic_info as any)?.photo_focal_x ?? 50,
      photo_focal_y: (basic_info as any)?.photo_focal_y ?? 50,
      photo_scale: (basic_info as any)?.photo_scale ?? 1,
      photo_fit_mode: ((basic_info as any)?.photo_fit_mode ?? 'cover') as 'cover' | 'contain',
      photo_gradient_side: ((basic_info as any)?.photo_gradient_side ?? 'left') as 'left' | 'right',
      photo_reveal: (basic_info as any)?.photo_reveal ?? 0.22,
      identity_text_layout: ((basic_info as any)?.identity_text_layout ?? 'side_strap') as
        | 'side_strap'
        | 'bottom_banner'
        | 'transparent',
      photo_mode: ((basic_info as any)?.photo_mode ?? 'original') as 'original' | 'cutout',
      photo_shadow: (basic_info as any)?.photo_shadow ?? false
    }
  }

  const [draftLook, setDraftLook] = useState(getCurrentLook())
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Keep ref updated with latest saveBasicSetup
  useEffect(() => {
    saveBasicSetupRef.current = saveBasicSetup
  }, [saveBasicSetup])

  useEffect(() => {
    if (isCustomizePanelOpen) setDraftLook(getCurrentLook())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomizePanelOpen])

  // Auto-save draftLook changes (debounced with sequence tracking)
  useEffect(() => {
    if (!isCustomizePanelOpen) return

    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    // Update local state immediately for preview
    updateBasicInfo({
      photo_focal_x: draftLook.photo_focal_x,
      photo_focal_y: draftLook.photo_focal_y,
      photo_scale: draftLook.photo_scale,
      photo_fit_mode: draftLook.photo_fit_mode,
      photo_gradient_side: draftLook.photo_gradient_side,
      photo_reveal: draftLook.photo_reveal,
      identity_text_layout: draftLook.identity_text_layout,
      photo_mode: draftLook.photo_mode,
      photo_shadow: draftLook.photo_shadow
    } as any)

    // Debounce the actual save to backend with sequence tracking
    setIsSavingCustomize(true)
    saveTimeoutRef.current = setTimeout(async () => {
      const currentSequence = ++customizeSequenceRef.current

      try {
        // Only save if this is still the most recent change
        if (currentSequence === customizeSequenceRef.current) {
          await saveBasicSetupRef.current()
        }
      } catch (e) {
        console.error('Failed to auto-save customization:', e)
        toaster.create({
          title: 'Customization save failed',
          description: e instanceof Error ? e.message : 'Could not save your customization changes. They will be retried.',
          type: 'error',
          duration: 5000
        })
      } finally {
        // Only clear saving state if we're still on the most recent sequence
        if (currentSequence === customizeSequenceRef.current) {
          setIsSavingCustomize(false)
        }
      }
    }, 1500) // 1.5 second debounce (unified with refinements)

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [draftLook, isCustomizePanelOpen, updateBasicInfo])

  const applyPreset = (preset: 'more_photo' | 'balanced' | 'more_background') => {
    if (preset === 'more_photo') {
      setDraftLook((prev) => ({ ...prev, photo_scale: 1.25, photo_reveal: 0.14 }))
      return
    }
    if (preset === 'more_background') {
      setDraftLook((prev) => ({ ...prev, photo_scale: 0.95, photo_reveal: 0.32 }))
      return
    }
    setDraftLook((prev) => ({ ...prev, photo_scale: 1.05, photo_reveal: 0.22 }))
  }

  const handleCloseCustomize = () => {
    // Ensure any pending save completes before closing
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }
    setIsDrawerOpen(false)
  }

  return (
    <OnboardingLayout
      currentStep={2}
      totalSteps={5}
      stepLabels={['Basic Setup', 'Preview', 'Landing page', 'Targeting', 'Select package']}
    >
      <VStack gap={8} align="stretch" maxW="6xl" mx="auto">
        {/* Header */}
        <VStack gap={4} textAlign="center">
          <Heading size={{ base: '2xl', md: '3xl' }} color="gray.900">
            Your Ad
          </Heading>
          {polishedCandidates.length > 0 && (
            <HStack justify="center" gap={2} flexWrap="wrap">
              {showOriginal && hasOriginalCandidates ? (
                <Badge variant="subtle" colorPalette="gray">
                  Showing original (unpolished)
                </Badge>
              ) : copyWasPolished ? (
                <Badge variant="subtle" colorPalette="purple">
                  AI-polished for clarity and ad compliance
                </Badge>
              ) : (
                <Badge variant="subtle" colorPalette="gray">
                  Story formatted from your inputs
                </Badge>
              )}
              {hasOriginalCandidates && copyWasPolished && (
                <Link
                  fontSize="xs"
                  color="gray.600"
                  textDecoration="underline"
                  onClick={() => setShowOriginal(!showOriginal)}
                  cursor="pointer"
                >
                  {showOriginal ? 'Show polished' : 'See original'}
                </Link>
              )}
            </HStack>
          )}
          <Text fontSize="lg" color="gray.600" maxW="2xl">
            Here’s your story-based carousel ad using what you shared. You can refine it below, then build the landing page your ad will send people to.
          </Text>
        </VStack>

        {/* Error Display */}
        {error && (
          <Box
            p={4}
            bg="red.50"
            borderWidth="1px"
            borderColor="red.200"
            borderRadius="md"
            color="red.700"
          >
            <Text fontSize="sm">{error}</Text>
          </Box>
        )}

        {isLoading && !error ? (
          <Box textAlign="center" py={20}>
            <VStack gap={4}>
              <Spinner size="xl" color="brand.500" />
              <Text fontSize="lg" color="gray.600">Generating your carousel preview...</Text>
            </VStack>
          </Box>
        ) : error && carouselCandidates.length === 0 ? (
          <Card.Root borderColor="red.200">
            <Card.Body p={8} textAlign="center">
              <VStack gap={4}>
                <Text fontSize="md" color="red.700" fontWeight="medium">
                  Failed to generate preview
                </Text>
                <Text fontSize="sm" color="gray.600">
                  {error}
                </Text>
                <Button colorPalette="brand" onClick={handleRetryGeneration}>
                  Try again
                </Button>
              </VStack>
            </Card.Body>
          </Card.Root>
        ) : currentCarousel ? (
          <>
          {/* Split layout: Carousel on left, inline panel on right (desktop only) */}
          <Flex 
            gap={6} 
            align="start"
            direction={{ base: 'column', lg: isCustomizePanelOpen ? 'row' : 'column' }}
          >
            {/* Left side: Carousel and story types */}
            <VStack 
              gap={6} 
              align="stretch" 
              flex={isCustomizePanelOpen ? { base: 1, lg: 1 } : 1}
              w="full"
            >
              {/* Main stage: Carousel */}
              <Box display="flex" justifyContent="center">
                <Box w="full" maxW={{ base: '420px', md: '560px', lg: isCustomizePanelOpen ? '500px' : '680px' }}>
                  <CarouselAdPreviewCard
                    key={currentAdIndex}
                    practitionerName={basic_info?.name || ''}
                    practitionerPhotoUrl={basic_info?.photo_url || ''}
                    practitionerPhotoCutoutUrl={(basic_info as any)?.photo_cutout_url}
                    whoYouHelp={basic_info?.who_you_help || ''}
                    primaryOutcome={basic_info?.offer_primary_outcome || ''}
                    templateName={currentCarousel.template_name}
                    slides={currentCarousel.slides}
                    bgPalette={selectedPalette as any}
                    photoFocalX={isCustomizePanelOpen ? draftLook.photo_focal_x : (basic_info as any)?.photo_focal_x}
                    photoFocalY={isCustomizePanelOpen ? draftLook.photo_focal_y : (basic_info as any)?.photo_focal_y}
                    photoScale={isCustomizePanelOpen ? draftLook.photo_scale : (basic_info as any)?.photo_scale}
                    photoFitMode={isCustomizePanelOpen ? draftLook.photo_fit_mode : (basic_info as any)?.photo_fit_mode}
                    photoGradientSide={isCustomizePanelOpen ? draftLook.photo_gradient_side : (basic_info as any)?.photo_gradient_side}
                    photoReveal={isCustomizePanelOpen ? draftLook.photo_reveal : (basic_info as any)?.photo_reveal}
                    identityTextLayout={
                      isCustomizePanelOpen ? draftLook.identity_text_layout : (basic_info as any)?.identity_text_layout
                    }
                    isAdjustingIdentityPhoto={isCustomizePanelOpen}
                    onPhotoFocalChange={(x, y) => setDraftLook((prev) => ({ ...prev, photo_focal_x: x, photo_focal_y: y }))}
                    onCustomizeClick={() => {
                      setActiveDrawerPanel('customizeLook')
                      setIsDrawerOpen(true)
                    }}
                    photoMode={isCustomizePanelOpen ? draftLook.photo_mode : ((basic_info as any)?.photo_mode ?? 'original')}
                    photoShadow={isCustomizePanelOpen ? draftLook.photo_shadow : photoShadow}
                  />
                </Box>
              </Box>

            {/* Story Types */}
            {carouselCandidates.length > 1 && (
              <VStack gap={3} align="stretch" maxW={{ base: '420px', md: '560px', lg: '680px' }} mx="auto">
                <VStack gap={1} align="stretch">
                  <HStack gap={1} align="center">
                    <Heading size="sm" color="gray.900">
                      Story types
                    </Heading>
                    <InfoTooltip
                      content="Different narrative approaches to tell your story. Each type structures your message differently to connect with your audience."
                      ariaLabel="Story types help"
                      maxW="300px"
                    />
                  </HStack>
                  <Text fontSize="sm" color="gray.600">
                    Choose how you want to tell your story. Each option uses a different narrative approach.
                  </Text>
                </VStack>
                <HStack gap={2} flexWrap="wrap">
                  {carouselCandidates.map((candidate, index) => (
                    <Tooltip.Root key={index}>
                      <Tooltip.Trigger asChild>
                        <Button
                          size="sm"
                          variant={index === currentAdIndex ? 'solid' : 'outline'}
                          colorPalette={index === currentAdIndex ? 'brand' : 'gray'}
                          onClick={() => setCurrentAdIndex(index)}
                        >
                          {candidate.template_name}
                        </Button>
                      </Tooltip.Trigger>
                      <Tooltip.Positioner>
                        <Tooltip.Content
                          bg="gray.900"
                          color="white"
                          px={3}
                          py={2}
                          borderRadius="md"
                          fontSize="sm"
                          maxW="300px"
                        >
                          <Tooltip.Arrow>
                            <Tooltip.ArrowTip fill="gray.900" />
                          </Tooltip.Arrow>
                          {getStoryTypeTooltip(candidate.template_name)}
                        </Tooltip.Content>
                      </Tooltip.Positioner>
                    </Tooltip.Root>
                  ))}
                </HStack>
              </VStack>
            )}

            {/* Refine CTA */}
            <Card.Root borderColor="gray.200" maxW={{ base: '420px', md: '560px', lg: '680px' }} mx="auto">
              <Card.Body p={5}>
                <VStack gap={3} align="stretch">
                  <HStack justify="space-between" align="center">
                    <HStack gap={1} align="center">
                      <Heading size="sm" color="gray.900">
                        Refine your story
                      </Heading>
                      <InfoTooltip
                        content="Add specific details like client moments, your approach, or testimonials to make your carousel more relatable. Changes auto-save."
                        ariaLabel="Refine your story help"
                      />
                    </HStack>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setActiveDrawerPanel('refineStory')
                        setIsDrawerOpen(true)
                      }}
                    >
                      Open
                    </Button>
                  </HStack>
                  <Text fontSize="sm" color="gray.600">
                    Add a few more details to make your carousel more specific and relatable.
                  </Text>
                </VStack>
              </Card.Body>
            </Card.Root>

            {/* "Why we use..." */}
            <Card.Root borderColor="gray.200" maxW={{ base: '420px', md: '560px', lg: '680px' }} mx="auto">
              <Card.Body p={0}>
                <Button
                  w="full"
                  variant="ghost"
                  justifyContent="space-between"
                  onClick={() => setIsWhyExpanded(!isWhyExpanded)}
                  p={4}
                  h="auto"
                >
                  <Heading size="sm" color="gray.900">
                    Why we use a story format + carousel ads
                  </Heading>
                  <Icon as={isWhyExpanded ? FiChevronUp : FiChevronDown} />
                </Button>
                {isWhyExpanded && (
                  <Box px={4} pb={4} pt={0}>
                    <VStack gap={3} align="stretch">
                      <Text fontSize="sm" color="gray.700">
                        People decide fast in-feed. A simple story helps the right person self-identify, understand the outcome,
                        and feel safe taking the next step.
                      </Text>
                      <VStack gap={2} align="stretch">
                        <Text fontSize="sm" color="gray.700">
                          <Text as="span" fontWeight="semibold">Story format:</Text> builds trust quickly (who you help → what they're dealing with → what changes → what to do next).
                        </Text>
                        <Text fontSize="sm" color="gray.700">
                          <Text as="span" fontWeight="semibold">Carousel:</Text> lets us deliver one clear message in "beats" so it's easier to read, more relatable, and more likely to convert than a single crowded image.
                        </Text>
                        <Text fontSize="sm" color="gray.700">
                          <Text as="span" fontWeight="semibold">Built for Meta:</Text> carousels tend to earn more engagement because the swipe is a micro-commitment — and it helps the algorithm learn who is responding.
                        </Text>
                      </VStack>
                    </VStack>
                  </Box>
                )}
              </Card.Body>
            </Card.Root>
            </VStack>

            {/* Inline Customize Look panel (desktop only) */}
            {isCustomizePanelOpen && activeDrawerPanel === 'customizeLook' && (
              <Box
                display={{ base: 'none', lg: 'block' }}
                w={{ lg: '380px', xl: '420px' }}
                flexShrink={0}
                position="sticky"
                top={4}
                alignSelf="flex-start"
              >
                <Card.Root
                  borderWidth="1px"
                  borderColor="gray.200"
                  bg="white"
                  boxShadow="lg"
                  borderRadius="xl"
                  overflow="hidden"
                >
                  {/* Panel Header */}
                  <Box
                    px={5}
                    py={4}
                    borderBottomWidth="1px"
                    borderColor="gray.200"
                    bg="gray.50"
                  >
                    <HStack justify="space-between" align="start">
                      <VStack align="stretch" gap={1}>
                        <Heading size="md">Customize look</Heading>
                        <Text fontSize="sm" color="gray.600">
                          Drag on the preview photo to reposition
                        </Text>
                      </VStack>
                      <IconButton
                        variant="ghost"
                        size="sm"
                        aria-label="Close panel"
                        onClick={() => setIsDrawerOpen(false)}
                      >
                        <FiX />
                      </IconButton>
                    </HStack>
                  </Box>

                  {/* Panel Body */}
                  <Box p={5} maxH="calc(100vh - 200px)" overflowY="auto">
                    <VStack align="stretch" gap={5}>
                      <VStack align="stretch" gap={3}>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.900">
                          Quick presets
                        </Text>
                        <HStack flexWrap="wrap" gap={2}>
                          <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                              <Button size="sm" variant="outline" onClick={() => applyPreset('more_photo')}>
                                More photo
                              </Button>
                            </Tooltip.Trigger>
                            <Tooltip.Positioner>
                              <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm" maxW="250px">
                                <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                                Maximize your photo visibility with less background overlay
                              </Tooltip.Content>
                            </Tooltip.Positioner>
                          </Tooltip.Root>
                          <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                              <Button size="sm" variant="outline" onClick={() => applyPreset('balanced')}>
                                Balanced
                              </Button>
                            </Tooltip.Trigger>
                            <Tooltip.Positioner>
                              <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm" maxW="250px">
                                <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                                A good mix of photo visibility and text readability
                              </Tooltip.Content>
                            </Tooltip.Positioner>
                          </Tooltip.Root>
                          <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                              <Button size="sm" variant="outline" onClick={() => applyPreset('more_background')}>
                                More background
                              </Button>
                            </Tooltip.Trigger>
                            <Tooltip.Positioner>
                              <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm" maxW="250px">
                                <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                                Emphasize the background color with a smaller photo
                              </Tooltip.Content>
                            </Tooltip.Positioner>
                          </Tooltip.Root>
                        </HStack>
                      </VStack>

                      <Separator />

                      <VStack align="stretch" gap={4}>
                        <HStack justify="space-between" align="center">
                          <Text fontSize="sm" fontWeight="semibold" color="gray.900">
                            Photo
                          </Text>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              setDraftLook((prev) => ({
                                ...prev,
                                photo_focal_x: 50,
                                photo_focal_y: 50,
                                photo_scale: 1,
                                photo_fit_mode: 'cover',
                                photo_gradient_side: 'left',
                                photo_reveal: 0.22,
                                identity_text_layout: 'side_strap'
                              }))
                            }
                          >
                            Reset
                          </Button>
                        </HStack>

                        <VStack align="stretch" gap={2}>
                          <HStack justify="space-between">
                            <Tooltip.Root>
                              <Tooltip.Trigger asChild>
                                <Text fontSize="sm" color="gray.700" cursor="help">
                                  Zoom
                                </Text>
                              </Tooltip.Trigger>
                              <Tooltip.Positioner>
                                <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm" maxW="250px">
                                  <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                                  Scale your photo larger or smaller within the frame
                                </Tooltip.Content>
                              </Tooltip.Positioner>
                            </Tooltip.Root>
                            <Text fontSize="sm" color="gray.600">
                              {Math.round(draftLook.photo_scale * 100)}%
                            </Text>
                          </HStack>
                          <Slider.Root
                            min={0.85}
                            max={1.6}
                            step={0.01}
                            value={[draftLook.photo_scale]}
                            onValueChange={(details: any) => {
                              const v = Array.isArray(details?.value) ? details.value[0] : details?.value
                              if (typeof v !== 'number' || !Number.isFinite(v)) return
                              setDraftLook((prev) => ({ ...prev, photo_scale: v }))
                            }}
                          >
                            <Slider.Control>
                              <Slider.Track bg="gray.200" h="1.5" borderRadius="full">
                                <Slider.Range bg="brand.600" borderRadius="full" />
                              </Slider.Track>
                              <Slider.Thumb index={0} />
                            </Slider.Control>
                          </Slider.Root>
                        </VStack>

                        <VStack align="stretch" gap={2}>
                          <HStack justify="space-between">
                            <Tooltip.Root>
                              <Tooltip.Trigger asChild>
                                <Text fontSize="sm" color="gray.700" cursor="help">
                                  Text readability
                                </Text>
                              </Tooltip.Trigger>
                              <Tooltip.Positioner>
                                <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm" maxW="250px">
                                  <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                                  Adjust the dark overlay behind text to make it easier to read
                                </Tooltip.Content>
                              </Tooltip.Positioner>
                            </Tooltip.Root>
                            <Text fontSize="sm" color="gray.600">
                              {Math.round(draftLook.photo_reveal * 100)}%
                            </Text>
                          </HStack>
                          <Slider.Root
                            min={0.05}
                            max={0.55}
                            step={0.01}
                            value={[draftLook.photo_reveal]}
                            onValueChange={(details: any) => {
                              const v = Array.isArray(details?.value) ? details.value[0] : details?.value
                              if (typeof v !== 'number' || !Number.isFinite(v)) return
                              setDraftLook((prev) => ({ ...prev, photo_reveal: v }))
                            }}
                          >
                            <Slider.Control>
                              <Slider.Track bg="gray.200" h="1.5" borderRadius="full">
                                <Slider.Range bg="brand.600" borderRadius="full" />
                              </Slider.Track>
                              <Slider.Thumb index={0} />
                            </Slider.Control>
                          </Slider.Root>
                        </VStack>

                        <VStack align="stretch" gap={2}>
                          <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                              <Text fontSize="sm" color="gray.700" cursor="help" w="fit-content">
                                Fit mode
                              </Text>
                            </Tooltip.Trigger>
                            <Tooltip.Positioner>
                              <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm" maxW="250px">
                                <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                                Choose how your photo fits in the frame
                              </Tooltip.Content>
                            </Tooltip.Positioner>
                          </Tooltip.Root>
                          <HStack gap={2}>
                            <Tooltip.Root>
                              <Tooltip.Trigger asChild>
                                <Button
                                  size="sm"
                                  variant={draftLook.photo_fit_mode === 'cover' ? 'solid' : 'outline'}
                                  colorPalette={draftLook.photo_fit_mode === 'cover' ? 'brand' : 'gray'}
                                  onClick={() => setDraftLook((prev) => ({ ...prev, photo_fit_mode: 'cover' }))}
                                >
                                  Fill
                                </Button>
                              </Tooltip.Trigger>
                              <Tooltip.Positioner>
                                <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm" maxW="250px">
                                  <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                                  Photo fills the entire frame, cropping edges if needed
                                </Tooltip.Content>
                              </Tooltip.Positioner>
                            </Tooltip.Root>
                            <Tooltip.Root>
                              <Tooltip.Trigger asChild>
                                <Button
                                  size="sm"
                                  variant={draftLook.photo_fit_mode === 'contain' ? 'solid' : 'outline'}
                                  colorPalette={draftLook.photo_fit_mode === 'contain' ? 'brand' : 'gray'}
                                  onClick={() => setDraftLook((prev) => ({ ...prev, photo_fit_mode: 'contain' }))}
                                >
                                  Fit
                                </Button>
                              </Tooltip.Trigger>
                              <Tooltip.Positioner>
                                <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm" maxW="250px">
                                  <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                                  Show the entire photo, with background color visible on edges
                                </Tooltip.Content>
                              </Tooltip.Positioner>
                            </Tooltip.Root>
                          </HStack>
                        </VStack>

                        {/* Background removal: Show either "Remove background" button OR toggle, never both */}
                        {(basic_info as any)?.photo_storage_path && !(basic_info as any)?.photo_cutout_url && (
                          <VStack align="stretch" gap={2}>
                            <HStack justify="space-between" align="center">
                              <Text fontSize="sm" color="gray.700">
                                Background
                              </Text>
                            </HStack>
                            <Button
                              size="sm"
                              variant="outline"
                              colorPalette="brand"
                              loading={isReprocessing}
                              disabled={isReprocessing}
                              onClick={async () => {
                                setIsReprocessing(true)
                                try {
                                  const storagePath = (basic_info as any)?.photo_storage_path
                                  if (!storagePath) {
                                    throw new Error('Photo storage path not found')
                                  }

                                  const token = getAuthToken()
                                  if (!token) {
                                    throw new Error('Authentication required')
                                  }

                                  const response = await fetch(`${apiConfig.baseURL}/api/v1/spotlight/onboarding/photo-commit`, {
                                    method: 'POST',
                                    headers: {
                                      'Authorization': `Bearer ${token}`,
                                      'Content-Type': 'application/json'
                                    },
                                    body: JSON.stringify({
                                      storage_path: storagePath
                                    })
                                  })

                                  if (!response.ok) {
                                    const errorData = await response.json().catch(() => ({}))
                                    throw new Error(errorData.detail || 'Failed to remove background')
                                  }

                                  const data = await response.json()
                                  if (data.photo_cutout_url) {
                                    updateBasicInfo({
                                      photo_cutout_url: data.photo_cutout_url
                                    } as any)
                                    // Auto-switch to cutout mode
                                    setDraftLook((prev) => ({ ...prev, photo_mode: 'cutout' }))
                                    toaster.create({
                                      title: 'Background removed',
                                      description: 'Your photo now has a transparent background.',
                                      type: 'success',
                                      duration: 5000
                                    })
                                  } else {
                                    toaster.create({
                                      title: 'Could not remove background',
                                      description: 'We were unable to process your photo. Please try a different image.',
                                      type: 'warning',
                                      duration: 7000
                                    })
                                  }
                                } catch (error) {
                                  console.error('Failed to remove background:', error)
                                  toaster.create({
                                    title: 'Something went wrong',
                                    description: 'Please try again.',
                                    type: 'error',
                                    duration: 5000
                                  })
                                } finally {
                                  setIsReprocessing(false)
                                }
                              }}
                            >
                              Remove background
                            </Button>
                            <Text fontSize="xs" color="gray.600">
                              Create a cleaner look by removing the background from your photo.
                            </Text>
                          </VStack>
                        )}

                        {(basic_info as any)?.photo_cutout_url && (
                          <VStack align="stretch" gap={2}>
                            <Tooltip.Root>
                              <Tooltip.Trigger asChild>
                                <Text fontSize="sm" color="gray.700" cursor="help" w="fit-content">
                                  Background
                                </Text>
                              </Tooltip.Trigger>
                              <Tooltip.Positioner>
                                <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm" maxW="250px">
                                  <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                                  Switch between your original photo and the version with background removed
                                </Tooltip.Content>
                              </Tooltip.Positioner>
                            </Tooltip.Root>
                            <HStack gap={2}>
                              <Tooltip.Root>
                                <Tooltip.Trigger asChild>
                                  <Button
                                    size="sm"
                                    variant={draftLook.photo_mode === 'cutout' ? 'solid' : 'outline'}
                                    colorPalette={draftLook.photo_mode === 'cutout' ? 'brand' : 'gray'}
                                    onClick={() => setDraftLook((prev) => ({ ...prev, photo_mode: 'cutout' }))}
                                  >
                                    Removed
                                  </Button>
                                </Tooltip.Trigger>
                                <Tooltip.Positioner>
                                  <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm" maxW="250px">
                                    <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                                    Use the transparent background version of your photo
                                  </Tooltip.Content>
                                </Tooltip.Positioner>
                              </Tooltip.Root>
                              <Tooltip.Root>
                                <Tooltip.Trigger asChild>
                                  <Button
                                    size="sm"
                                    variant={draftLook.photo_mode === 'original' ? 'solid' : 'outline'}
                                    colorPalette={draftLook.photo_mode === 'original' ? 'brand' : 'gray'}
                                    onClick={() => setDraftLook((prev) => ({ ...prev, photo_mode: 'original' }))}
                                  >
                                    Original
                                  </Button>
                                </Tooltip.Trigger>
                                <Tooltip.Positioner>
                                  <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm" maxW="250px">
                                    <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                                    Use your original photo with its background intact
                                  </Tooltip.Content>
                                </Tooltip.Positioner>
                              </Tooltip.Root>
                            </HStack>
                          </VStack>
                        )}

                        <VStack align="stretch" gap={2}>
                          <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                              <Text fontSize="sm" color="gray.700" cursor="help" w="fit-content">
                                Background side
                              </Text>
                            </Tooltip.Trigger>
                            <Tooltip.Positioner>
                              <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm" maxW="250px">
                                <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                                Choose which side shows the colored background gradient
                              </Tooltip.Content>
                            </Tooltip.Positioner>
                          </Tooltip.Root>
                          <HStack gap={2}>
                            <Tooltip.Root>
                              <Tooltip.Trigger asChild>
                                <Button
                                  size="sm"
                                  variant={draftLook.photo_gradient_side === 'left' ? 'solid' : 'outline'}
                                  colorPalette={draftLook.photo_gradient_side === 'left' ? 'brand' : 'gray'}
                                  onClick={() => setDraftLook((prev) => ({ ...prev, photo_gradient_side: 'left' }))}
                                >
                                  Left
                                </Button>
                              </Tooltip.Trigger>
                              <Tooltip.Positioner>
                                <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm" maxW="250px">
                                  <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                                  Text appears on the left side over the gradient
                                </Tooltip.Content>
                              </Tooltip.Positioner>
                            </Tooltip.Root>
                            <Tooltip.Root>
                              <Tooltip.Trigger asChild>
                                <Button
                                  size="sm"
                                  variant={draftLook.photo_gradient_side === 'right' ? 'solid' : 'outline'}
                                  colorPalette={draftLook.photo_gradient_side === 'right' ? 'brand' : 'gray'}
                                  onClick={() => setDraftLook((prev) => ({ ...prev, photo_gradient_side: 'right' }))}
                                >
                                  Right
                                </Button>
                              </Tooltip.Trigger>
                              <Tooltip.Positioner>
                                <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm" maxW="250px">
                                  <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                                  Text appears on the right side over the gradient
                                </Tooltip.Content>
                              </Tooltip.Positioner>
                            </Tooltip.Root>
                          </HStack>
                        </VStack>

                        <VStack align="stretch" gap={2}>
                          <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                              <Text fontSize="sm" color="gray.700" cursor="help" w="fit-content">
                                Identity text layout
                              </Text>
                            </Tooltip.Trigger>
                            <Tooltip.Positioner>
                              <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm" maxW="250px">
                                <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                                Choose how your name and specialty appear on the first slide
                              </Tooltip.Content>
                            </Tooltip.Positioner>
                          </Tooltip.Root>
                          <HStack gap={2} flexWrap="wrap">
                            <Tooltip.Root>
                              <Tooltip.Trigger asChild>
                                <Button
                                  size="sm"
                                  variant={draftLook.identity_text_layout === 'side_strap' ? 'solid' : 'outline'}
                                  colorPalette={draftLook.identity_text_layout === 'side_strap' ? 'brand' : 'gray'}
                                  onClick={() => setDraftLook((prev) => ({ ...prev, identity_text_layout: 'side_strap' }))}
                                >
                                  Side strap
                                </Button>
                              </Tooltip.Trigger>
                              <Tooltip.Positioner>
                                <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm" maxW="250px">
                                  <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                                  Text in a vertical strip on the side (recommended)
                                </Tooltip.Content>
                              </Tooltip.Positioner>
                            </Tooltip.Root>
                            <Tooltip.Root>
                              <Tooltip.Trigger asChild>
                                <Button
                                  size="sm"
                                  variant={draftLook.identity_text_layout === 'bottom_banner' ? 'solid' : 'outline'}
                                  colorPalette={draftLook.identity_text_layout === 'bottom_banner' ? 'brand' : 'gray'}
                                  onClick={() => setDraftLook((prev) => ({ ...prev, identity_text_layout: 'bottom_banner' }))}
                                >
                                  Bottom banner
                                </Button>
                              </Tooltip.Trigger>
                              <Tooltip.Positioner>
                                <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm" maxW="250px">
                                  <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                                  Text in a horizontal bar at the bottom for maximum readability
                                </Tooltip.Content>
                              </Tooltip.Positioner>
                            </Tooltip.Root>
                            <Tooltip.Root>
                              <Tooltip.Trigger asChild>
                                <Button
                                  size="sm"
                                  variant={draftLook.identity_text_layout === 'transparent' ? 'solid' : 'outline'}
                                  colorPalette={draftLook.identity_text_layout === 'transparent' ? 'brand' : 'gray'}
                                  onClick={() => setDraftLook((prev) => ({ ...prev, identity_text_layout: 'transparent' }))}
                                >
                                  Transparent
                                </Button>
                              </Tooltip.Trigger>
                              <Tooltip.Positioner>
                                <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm" maxW="250px">
                                  <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                                  Text directly on the photo with a shadow for contrast
                                </Tooltip.Content>
                              </Tooltip.Positioner>
                            </Tooltip.Root>
                          </HStack>
                        </VStack>

                        <Separator />

                        <VStack align="stretch" gap={2}>
                          <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                              <Text fontSize="sm" color="gray.700" fontWeight="semibold" cursor="help" w="fit-content">
                                Background color
                              </Text>
                            </Tooltip.Trigger>
                            <Tooltip.Positioner>
                              <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm" maxW="250px">
                                <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                                Set the gradient color that appears behind the text on each slide
                              </Tooltip.Content>
                            </Tooltip.Positioner>
                          </Tooltip.Root>
                          <Flex wrap="wrap" gap={2}>
                            {[
                              { id: 'brand', label: 'Green', gradient: 'linear(to-br, brand.700, brand.400)', description: 'Professional green - builds trust and calm' },
                              { id: 'accent', label: 'Blue', gradient: 'linear(to-br, blue.700, blue.400)', description: 'Classic blue - reliable and confident' },
                              { id: 'teal', label: 'Teal', gradient: 'linear(to-br, teal.700, teal.400)', description: 'Fresh teal - modern and approachable' },
                              { id: 'yellow', label: 'Gold', gradient: 'linear(to-br, yellow.600, yellow.300)', description: 'Warm gold - energetic and optimistic' },
                              { id: 'gray', label: 'Slate', gradient: 'linear(to-br, gray.800, gray.500)', description: 'Neutral slate - minimal and sophisticated' }
                            ].map((p) => (
                              <Tooltip.Root key={p.id}>
                                <Tooltip.Trigger asChild>
                                  <Button
                                    size="sm"
                                    variant={selectedPalette === p.id ? 'solid' : 'outline'}
                                    colorPalette={selectedPalette === p.id ? 'brand' : 'gray'}
                                    borderRadius="full"
                                    onClick={() => handleSelectPalette(p.id)}
                                    disabled={isSavingPalette || isSavingBasics}
                                  >
                                    <HStack gap={2}>
                                      <Box w="14px" h="14px" borderRadius="full" bgGradient={p.gradient} />
                                      <Text>{p.label}</Text>
                                    </HStack>
                                  </Button>
                                </Tooltip.Trigger>
                                <Tooltip.Positioner>
                                  <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm" maxW="250px">
                                    <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                                    {p.description}
                                  </Tooltip.Content>
                                </Tooltip.Positioner>
                              </Tooltip.Root>
                            ))}
                          </Flex>
                        </VStack>
                      </VStack>
                    </VStack>
                  </Box>

                  {/* Panel Footer */}
                  <Box
                    px={5}
                    py={4}
                    borderTopWidth="1px"
                    borderColor="gray.200"
                    bg="gray.50"
                  >
                    <HStack justify="space-between" w="full" align="center">
                      {isSavingCustomize ? (
                        <Text fontSize="sm" color="gray.600">
                          Saving...
                        </Text>
                      ) : (
                        <Text fontSize="sm" color="gray.500">
                          Changes save automatically
                        </Text>
                      )}
                      <Button
                        size="sm"
                        colorPalette="brand"
                        onClick={() => {
                          setActiveDrawerPanel('refineStory')
                        }}
                      >
                        Refine story
                      </Button>
                    </HStack>
                  </Box>
                </Card.Root>
              </Box>
            )}
          </Flex>

          {/* Side drawer (mobile only for customize, always for refine story) */}
          <OnboardingSideDrawer
            open={isDrawerOpen && (activeDrawerPanel === 'refineStory' || (activeDrawerPanel === 'customizeLook' && !isLargeScreen))}
            onOpenChange={(e) => setIsDrawerOpen(e.open)}
            header={
              activeDrawerPanel === 'customizeLook' ? (
                <VStack align="stretch" gap={1}>
                  <Heading size="md">Customize look</Heading>
                  <Text fontSize="sm" color="gray.600">
                    Drag the identity photo in the preview while this panel is open.
                  </Text>
                </VStack>
              ) : (
                <VStack align="stretch" gap={1}>
                  <Heading size="md">Refine your story</Heading>
                  <Text fontSize="sm" color="gray.600">
                    Add a few specific details. Changes auto-save as you type.
                  </Text>
                </VStack>
              )
            }
            footer={
              activeDrawerPanel === 'customizeLook' ? (
                <HStack justify="space-between" w="full" align="center">
                  {isSavingCustomize ? (
                    <Text fontSize="sm" color="gray.600">
                      Saving...
                    </Text>
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      Changes save automatically
                    </Text>
                  )}
                  <Button
                    colorPalette="brand"
                    onClick={() => setActiveDrawerPanel('refineStory')}
                  >
                    Refine story
                  </Button>
                </HStack>
              ) : (
                <HStack justify="space-between" w="full" align="center">
                  <Text fontSize="sm" color="gray.500">
                    Changes save automatically
                  </Text>
                  <HStack gap={2}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setActiveDrawerPanel('customizeLook')}
                    >
                      Customize look
                    </Button>
                    <Button
                      colorPalette="brand"
                      onClick={async () => {
                        try {
                          await generatePreview()
                          // Reset carousel to first slide
                          setCurrentAdIndex(0)
                          // Close the drawer
                          setIsDrawerOpen(false)
                          toaster.create({
                            title: 'Preview updated',
                            description: 'Your carousel has been regenerated with your refinements.',
                            type: 'success',
                            duration: 5000
                          })
                        } catch (e) {
                          console.error('Failed to regenerate preview:', e)
                          toaster.create({
                            title: 'Regeneration failed',
                            description: e instanceof Error ? e.message : 'Could not regenerate preview. Please try again.',
                            type: 'error',
                            duration: 7000
                          })
                        }
                      }}
                      loading={isLoading}
                      disabled={isLoading}
                    >
                      Regenerate preview
                    </Button>
                  </HStack>
                </HStack>
              )
            }
          >
            {activeDrawerPanel === 'customizeLook' ? (
              <VStack align="stretch" gap={5}>
                        <VStack align="stretch" gap={3}>
                          <Text fontSize="sm" fontWeight="semibold" color="gray.900">
                            Quick presets
                          </Text>
                          <HStack flexWrap="wrap" gap={2}>
                            <Button size="sm" variant="outline" onClick={() => applyPreset('more_photo')}>
                              More photo
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => applyPreset('balanced')}>
                              Balanced
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => applyPreset('more_background')}>
                              More background
                            </Button>
                          </HStack>
                        </VStack>

                        <Separator />

                        <VStack align="stretch" gap={4}>
                          <HStack justify="space-between" align="center">
                            <Text fontSize="sm" fontWeight="semibold" color="gray.900">
                              Photo
                            </Text>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                setDraftLook((prev) => ({
                                  ...prev,
                                  photo_focal_x: 50,
                                  photo_focal_y: 50,
                                  photo_scale: 1,
                                  photo_fit_mode: 'cover',
                                  photo_gradient_side: 'left',
                                  photo_reveal: 0.22,
                                  identity_text_layout: 'side_strap'
                                }))
                              }
                            >
                              Reset
                            </Button>
                          </HStack>

                          <Box bg="brand.50" borderRadius="md" p={3} borderWidth="1px" borderColor="brand.200">
                            <HStack gap={2}>
                              <Icon as={FiMove} color="brand.600" />
                              <Text fontSize="sm" color="brand.700">
                                Drag on the preview image to reposition your photo
                              </Text>
                            </HStack>
                          </Box>

                          <VStack align="stretch" gap={2}>
                            <HStack justify="space-between">
                              <Text fontSize="sm" color="gray.700">
                                Zoom
                              </Text>
                              <Text fontSize="sm" color="gray.600">
                                {Math.round(draftLook.photo_scale * 100)}%
                              </Text>
                            </HStack>
                            <Slider.Root
                              min={0.85}
                              max={1.6}
                              step={0.01}
                              value={[draftLook.photo_scale]}
                              onValueChange={(details: any) => {
                                const v = Array.isArray(details?.value) ? details.value[0] : details?.value
                                if (typeof v !== 'number' || !Number.isFinite(v)) return
                                setDraftLook((prev) => ({ ...prev, photo_scale: v }))
                              }}
                            >
                              <Slider.Control>
                                <Slider.Track bg="gray.200" h="1.5" borderRadius="full">
                                  <Slider.Range bg="brand.600" borderRadius="full" />
                                </Slider.Track>
                                <Slider.Thumb index={0} />
                              </Slider.Control>
                            </Slider.Root>
                          </VStack>

                          <VStack align="stretch" gap={2}>
                            <HStack justify="space-between">
                              <Text fontSize="sm" color="gray.700">
                                Text readability
                              </Text>
                              <Text fontSize="sm" color="gray.600">
                                {Math.round(draftLook.photo_reveal * 100)}%
                              </Text>
                            </HStack>
                            <Slider.Root
                              min={0.05}
                              max={0.55}
                              step={0.01}
                              value={[draftLook.photo_reveal]}
                              onValueChange={(details: any) => {
                                const v = Array.isArray(details?.value) ? details.value[0] : details?.value
                                if (typeof v !== 'number' || !Number.isFinite(v)) return
                                setDraftLook((prev) => ({ ...prev, photo_reveal: v }))
                              }}
                            >
                              <Slider.Control>
                                <Slider.Track bg="gray.200" h="1.5" borderRadius="full">
                                  <Slider.Range bg="brand.600" borderRadius="full" />
                                </Slider.Track>
                                <Slider.Thumb index={0} />
                              </Slider.Control>
                            </Slider.Root>
                            <Text fontSize="xs" color="gray.600">
                              Controls the width of the dark scrim behind the identity text.
                            </Text>
                          </VStack>

                          <VStack align="stretch" gap={2}>
                            <Text fontSize="sm" color="gray.700">
                              Fit mode
                            </Text>
                            <HStack gap={2}>
                              <Button
                                size="sm"
                                variant={draftLook.photo_fit_mode === 'cover' ? 'solid' : 'outline'}
                                colorPalette={draftLook.photo_fit_mode === 'cover' ? 'brand' : 'gray'}
                                onClick={() => setDraftLook((prev) => ({ ...prev, photo_fit_mode: 'cover' }))}
                              >
                                Fill
                              </Button>
                              <Button
                                size="sm"
                                variant={draftLook.photo_fit_mode === 'contain' ? 'solid' : 'outline'}
                                colorPalette={draftLook.photo_fit_mode === 'contain' ? 'brand' : 'gray'}
                                onClick={() => setDraftLook((prev) => ({ ...prev, photo_fit_mode: 'contain' }))}
                              >
                                Fit
                              </Button>
                            </HStack>
                          </VStack>

                          {/* Background removal: Show either "Remove background" button OR toggle, never both */}
                          {(basic_info as any)?.photo_storage_path && !(basic_info as any)?.photo_cutout_url && (
                            <VStack align="stretch" gap={2}>
                              <HStack justify="space-between" align="center">
                                <Text fontSize="sm" color="gray.700">
                                  Background
                                </Text>
                              </HStack>
                              <Button
                                size="sm"
                                variant="outline"
                                colorPalette="brand"
                                loading={isReprocessing}
                                disabled={isReprocessing}
                                onClick={async () => {
                                  setIsReprocessing(true)
                                  try {
                                    const storagePath = (basic_info as any)?.photo_storage_path
                                    if (!storagePath) {
                                      throw new Error('Photo storage path not found')
                                    }

                                    const token = getAuthToken()
                                    if (!token) {
                                      throw new Error('Authentication required')
                                    }

                                    const response = await fetch(`${apiConfig.baseURL}/api/v1/spotlight/onboarding/photo-commit`, {
                                      method: 'POST',
                                      headers: {
                                        'Authorization': `Bearer ${token}`,
                                        'Content-Type': 'application/json'
                                      },
                                      body: JSON.stringify({
                                        storage_path: storagePath
                                      })
                                    })

                                    if (!response.ok) {
                                      const errorData = await response.json().catch(() => ({}))
                                      throw new Error(errorData.detail || 'Failed to remove background')
                                    }

                                    const data = await response.json()
                                    if (data.photo_cutout_url) {
                                      updateBasicInfo({
                                        photo_cutout_url: data.photo_cutout_url
                                      } as any)
                                      // Auto-switch to cutout mode
                                      setDraftLook((prev) => ({ ...prev, photo_mode: 'cutout' }))
                                      toaster.create({
                                        title: 'Background removed',
                                        description: 'Your photo now has a transparent background.',
                                        type: 'success',
                                        duration: 5000
                                      })
                                    } else {
                                      toaster.create({
                                        title: 'Could not remove background',
                                        description: 'We were unable to process your photo. Please try a different image.',
                                        type: 'warning',
                                        duration: 7000
                                      })
                                    }
                                  } catch (error) {
                                    console.error('Failed to remove background:', error)
                                    toaster.create({
                                      title: 'Something went wrong',
                                      description: 'Please try again.',
                                      type: 'error',
                                      duration: 5000
                                    })
                                  } finally {
                                    setIsReprocessing(false)
                                  }
                                }}
                              >
                                Remove background
                              </Button>
                              <Text fontSize="xs" color="gray.600">
                                Create a cleaner look by removing the background from your photo.
                              </Text>
                            </VStack>
                          )}

                          {(basic_info as any)?.photo_cutout_url && (
                            <VStack align="stretch" gap={2}>
                              <Text fontSize="sm" color="gray.700">
                                Background
                              </Text>
                              <HStack gap={2}>
                                <Button
                                  size="sm"
                                  variant={draftLook.photo_mode === 'cutout' ? 'solid' : 'outline'}
                                  colorPalette={draftLook.photo_mode === 'cutout' ? 'brand' : 'gray'}
                                  onClick={() => setDraftLook((prev) => ({ ...prev, photo_mode: 'cutout' }))}
                                >
                                  Removed
                                </Button>
                                <Button
                                  size="sm"
                                  variant={draftLook.photo_mode === 'original' ? 'solid' : 'outline'}
                                  colorPalette={draftLook.photo_mode === 'original' ? 'brand' : 'gray'}
                                  onClick={() => setDraftLook((prev) => ({ ...prev, photo_mode: 'original' }))}
                                >
                                  Original
                                </Button>
                              </HStack>
                            </VStack>
                          )}

                          <VStack align="stretch" gap={2}>
                            <HStack justify="space-between">
                              <Text fontSize="sm" color="gray.700">
                                Add shadow
                              </Text>
                              <Button
                                size="sm"
                                variant={draftLook.photo_shadow ? 'solid' : 'outline'}
                                colorPalette={draftLook.photo_shadow ? 'brand' : 'gray'}
                                onClick={() => setDraftLook((prev) => ({ ...prev, photo_shadow: !prev.photo_shadow }))}
                              >
                                {draftLook.photo_shadow ? 'On' : 'Off'}
                              </Button>
                            </HStack>
                            <Text fontSize="xs" color="gray.600">
                              Adds a drop shadow to improve readability on light backgrounds.
                            </Text>
                          </VStack>

                          <VStack align="stretch" gap={2}>
                            <Text fontSize="sm" color="gray.700">
                              Background side
                            </Text>
                            <HStack gap={2}>
                              <Button
                                size="sm"
                                variant={draftLook.photo_gradient_side === 'left' ? 'solid' : 'outline'}
                                colorPalette={draftLook.photo_gradient_side === 'left' ? 'brand' : 'gray'}
                                onClick={() => setDraftLook((prev) => ({ ...prev, photo_gradient_side: 'left' }))}
                              >
                                Left
                              </Button>
                              <Button
                                size="sm"
                                variant={draftLook.photo_gradient_side === 'right' ? 'solid' : 'outline'}
                                colorPalette={draftLook.photo_gradient_side === 'right' ? 'brand' : 'gray'}
                                onClick={() => setDraftLook((prev) => ({ ...prev, photo_gradient_side: 'right' }))}
                              >
                                Right
                              </Button>
                            </HStack>
                          </VStack>

                          <VStack align="stretch" gap={2}>
                            <Text fontSize="sm" color="gray.700">
                              Identity text layout
                            </Text>
                            <HStack gap={2} flexWrap="wrap">
                              <Button
                                size="sm"
                                variant={draftLook.identity_text_layout === 'side_strap' ? 'solid' : 'outline'}
                                colorPalette={draftLook.identity_text_layout === 'side_strap' ? 'brand' : 'gray'}
                                onClick={() => setDraftLook((prev) => ({ ...prev, identity_text_layout: 'side_strap' }))}
                              >
                                Side strap
                              </Button>
                              <Button
                                size="sm"
                                variant={draftLook.identity_text_layout === 'bottom_banner' ? 'solid' : 'outline'}
                                colorPalette={draftLook.identity_text_layout === 'bottom_banner' ? 'brand' : 'gray'}
                                onClick={() => setDraftLook((prev) => ({ ...prev, identity_text_layout: 'bottom_banner' }))}
                              >
                                Bottom banner
                              </Button>
                              <Button
                                size="sm"
                                variant={draftLook.identity_text_layout === 'transparent' ? 'solid' : 'outline'}
                                colorPalette={draftLook.identity_text_layout === 'transparent' ? 'brand' : 'gray'}
                                onClick={() => setDraftLook((prev) => ({ ...prev, identity_text_layout: 'transparent' }))}
                              >
                                Transparent
                              </Button>
                            </HStack>
                            <Text fontSize="xs" color="gray.600">
                              Side strap is recommended. Bottom banner maximizes readability. Transparent renders text directly on the photo with shadow for contrast.
                            </Text>
                          </VStack>

                          <Separator />

                          <VStack align="stretch" gap={2}>
                            <Text fontSize="sm" color="gray.700" fontWeight="semibold">
                              Background color
                            </Text>
                            <Flex wrap="wrap" gap={2}>
                              {[
                                { id: 'brand', label: 'Green', gradient: 'linear(to-br, brand.700, brand.400)' },
                                { id: 'accent', label: 'Blue', gradient: 'linear(to-br, blue.700, blue.400)' },
                                { id: 'teal', label: 'Teal', gradient: 'linear(to-br, teal.700, teal.400)' },
                                { id: 'yellow', label: 'Gold', gradient: 'linear(to-br, yellow.600, yellow.300)' },
                                { id: 'gray', label: 'Slate', gradient: 'linear(to-br, gray.800, gray.500)' }
                              ].map((p) => (
                                <Button
                                  key={p.id}
                                  size="sm"
                                  variant={selectedPalette === p.id ? 'solid' : 'outline'}
                                  colorPalette={selectedPalette === p.id ? 'brand' : 'gray'}
                                  borderRadius="full"
                                  onClick={() => handleSelectPalette(p.id)}
                                  disabled={isSavingPalette || isSavingBasics}
                                >
                                  <HStack gap={2}>
                                    <Box w="14px" h="14px" borderRadius="full" bgGradient={p.gradient} />
                                    <Text>{p.label}</Text>
                                  </HStack>
                                </Button>
                              ))}
                            </Flex>
                          </VStack>
                        </VStack>
                      </VStack>
            ) : (
              <Card.Root
                id="refinements-section"
                bg="gray.50"
                borderColor="gray.200"
                ref={(node: any) => {
                  refinementsSectionRef.current = node
                }}
              >
                <Card.Body p={6}>
                  <VStack gap={4} align="stretch">
                    <Text fontSize="sm" color="gray.700">
                      Add a few more details to make your carousel more specific and relatable. Auto-saves as you type.
                    </Text>
                    <SimpleGrid columns={{ base: 1 }} gap={4} mt={2}>
                      {improvementActions.map((action) => {
                                const currentValue = getFieldValue(detailed_interview, action.id)
                                // Keep card expanded if it has content or is explicitly expanded
                                const hasContent = currentValue.trim().length > 0
                                const isExpanded = expandedActionId === action.id || hasContent
                                const isHovered = hoveredActionId === action.id
                                const displayValue = expandedActionId === action.id ? actionValue : currentValue
                                const charLimit = getActionLimit(action.id)

                                return (
                                  <Card.Root
                                    key={action.id}
                                    borderWidth="2px"
                                    borderColor={isExpanded ? 'brand.500' : 'gray.200'}
                                    bg={isExpanded ? 'brand.50' : 'white'}
                                    _hover={{ borderColor: 'brand.300', shadow: 'sm', cursor: 'pointer' }}
                                    transition="border-color 0.2s, background-color 0.2s"
                                    onClick={() => handleActionClick(action.id)}
                                    onMouseEnter={() => setHoveredActionId(action.id)}
                                    onMouseLeave={() => setHoveredActionId(null)}
                                  >
                                    <Card.Body p={4}>
                                      <VStack align="start" gap={3}>
                                        <Heading size="sm" color="gray.900">
                                          {action.title}
                                        </Heading>
                                        <Text fontSize="xs" color="gray.600">
                                          {action.description}
                                        </Text>

                                        {/* Show textarea on hover or when expanded */}
                                        {(isHovered || isExpanded) && (
                                          <Box
                                            w="full"
                                            pt={3}
                                            borderTopWidth="1px"
                                            borderTopColor={isExpanded ? 'brand.300' : 'gray.200'}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <VStack gap={3} align="stretch">
                                              <Box>
                                                <Text fontSize="xs" color="gray.700" mb={2} fontWeight="medium">
                                                  {action.id === 'before_moment' &&
                                                    (onboardingMode === 'product'
                                                      ? 'What\'s the most common "before" moment before they try it?'
                                                      : 'What\'s the most common "before" moment?')}
                                                  {action.id === 'turning_point' &&
                                                    (onboardingMode === 'product'
                                                      ? 'What\'s the turning point that makes them decide to try something new?'
                                                      : 'What\'s the turning point that makes someone seek help?')}
                                                  {action.id === 'approach_one_liner' &&
                                                    (onboardingMode === 'product'
                                                      ? 'Explain how it works in one sentence (plain language):'
                                                      : 'Explain your approach in one sentence (plain language):')}
                                                  {action.id === 'first_call_expectations' &&
                                                    (onboardingMode === 'product'
                                                      ? 'What should they do next after clicking?'
                                                      : 'What happens on the first call?')}
                                                  {action.id === 'testimonial' && 'Paste one real testimonial:'}
                                                  {action.id === 'exclusions' && 'Who is this not for?'}
                                                  {action.id === 'origin_story' && 'Why do you do this work?'}
                                                </Text>
                                                <ImproveableTextarea
                                                  value={displayValue}
                                                  onChange={(value) => {
                                                    if (expandedActionId === action.id) {
                                                      handleActionValueChange(value, action.id)
                                                    } else {
                                                      // On hover, clicking textarea should expand
                                                      handleActionClick(action.id)
                                                      setActionValue(value)
                                                    }
                                                  }}
                                                  context={action.id}
                                                  fieldType={
                                                    action.id === 'before_moment' || action.id === 'turning_point' || action.id === 'approach_one_liner'
                                                      ? 'ad_body'
                                                      : 'general'
                                                  }
                                                  onboardingMode={onboardingMode}
                                                  maxLength={charLimit}
                                                  placeholder={
                                                    action.id === 'before_moment'
                                                      ? (onboardingMode === 'product'
                                                        ? 'e.g., "I\'ve tried everything and I still can\'t sleep."'
                                                        : 'e.g., "I\'m exhausted and nothing I try seems to work."')
                                                      : action.id === 'turning_point'
                                                        ? (onboardingMode === 'product'
                                                          ? 'e.g., "I realized I need something simple I can stick to."'
                                                          : 'e.g., "I realized I can\'t keep powering through like this."')
                                                        : action.id === 'approach_one_liner'
                                                          ? (onboardingMode === 'product'
                                                            ? 'e.g., "Supports calm evenings so you can fall asleep faster."'
                                                            : 'e.g., "We identify what\'s driving the issue and build a simple plan you can follow."')
                                                          : action.id === 'first_call_expectations'
                                                            ? (onboardingMode === 'product'
                                                              ? 'e.g., "Start with one serving nightly for 7 days and track how you feel."'
                                                              : 'e.g., "We\'ll review your goals, what you\'ve tried, and map a clear next step."')
                                                            : action.id === 'testimonial'
                                                              ? 'e.g., "I had more energy in 3 weeks and finally slept through the night."'
                                                              : action.id === 'exclusions'
                                                                ? 'e.g., "Not for anyone looking for a quick fix."'
                                                                : action.id === 'origin_story'
                                                                  ? 'e.g., "I struggled with the same challenges my clients face, and I learned what actually works."'
                                                                  : 'Type your answer here...'
                                                  }
                                                  size="sm"
                                                  minH="80px"
                                                  rows={3}
                                                  resize="none"
                                                  overflow="hidden"
                                                  bg="white"
                                                  onFocus={() => {
                                                    if (expandedActionId !== action.id) {
                                                      handleActionClick(action.id)
                                                    }
                                                  }}
                                                  autoFocus={isExpanded}
                                                />
                                              </Box>
                                            </VStack>
                                          </Box>
                                        )}
                                      </VStack>
                                    </Card.Body>
                                  </Card.Root>
                                )
                              })}
                    </SimpleGrid>
                  </VStack>
                </Card.Body>
              </Card.Root>
            )}
          </OnboardingSideDrawer>
          </>
        ) : (
          <Card.Root borderColor="gray.200">
            <Card.Body p={8} textAlign="center">
              <VStack gap={3}>
                <Text fontSize="md" color="gray.800" fontWeight="medium">
                  Your preview hasn’t been generated yet
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Click below to generate your carousel preview from your saved basics.
                </Text>
                <Button colorPalette="brand" onClick={handleRetryGeneration}>
                  Generate preview
                </Button>
              </VStack>
            </Card.Body>
          </Card.Root>
        )}

        {/* Action Buttons - Always visible when we have content */}
        {currentCarousel && (
          <HStack gap={4} justify="space-between" pt={4} w="full">
            <Button
              variant="ghost"
              onClick={handleBackToBasics}
            >
              <Icon as={FiArrowLeft} />
              Back (edit basics)
            </Button>
            <Button
              size="lg"
              colorPalette="brand"
              onClick={handleContinue}
              loading={isSavingBasics}
            >
              Continue to landing page
              <Icon as={FiArrowRight} />
            </Button>
          </HStack>
        )}
      </VStack>
      <Toaster toaster={toaster}>{() => null}</Toaster>
    </OnboardingLayout>
  )
}
